import { PullRefresh } from '@apps-common/components/common'
import { useWindowDimensions } from '@apps-common/hooks/useWindowDimensions'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'
import { useEffect, useRef, useState } from 'react'
import { SiteEvents } from '../site-events'
import { SiteManageMyMonitoringContainer } from '../site-manage'

interface IProps {
  loading: boolean
  refetch: () => void
  site?: IMMSiteModel
  setSiteId: (id: string) => void
  setView: (key: string) => void
}

export const SiteHomeView: React.FC<IProps> = ({ loading, refetch, site, setSiteId, setView }) => {
  const [refetchRegistrations, setRefetchRegistrations] = useState<{ id: string; refetch: () => void }[]>([])
  const [manageHeight, setManageHeight] = useState<number>()
  const manageContainerRef = useRef<HTMLDivElement>(null)
  const { width } = useWindowDimensions()

  const shouldCapEventsToManageHeight = !!width && width >= 768

  const registerRefetch = (caller: { id: string; refetch: () => void }) => {
    setRefetchRegistrations((prev) => {
      const cloned = prev.filter((p) => p.id !== caller.id)

      return [...cloned, caller]
    })
  }

  const refetchAll = () => {
    refetch()
    refetchRegistrations.forEach((r) => r.refetch())
  }

  useEffect(() => {
    if (!shouldCapEventsToManageHeight) {
      setManageHeight(undefined)
      return
    }

    const element = manageContainerRef.current
    if (!element) return

    const updateHeight = () => {
      const nextHeight = Math.ceil(element.getBoundingClientRect().height)
      setManageHeight((current) => (current === nextHeight ? current : nextHeight))
    }

    updateHeight()

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null
    resizeObserver?.observe(element)
    window.addEventListener('resize', updateHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateHeight)
    }
  }, [shouldCapEventsToManageHeight, loading, site])

  return (
    <div className='h-full min-h-0'>
      <PullRefresh loading={loading} callback={refetchAll} />
      <div className='grid h-full md:h-auto min-h-0 grid-rows-[auto_minmax(0,1fr)] max-w-[430px] mx-auto lg:max-w-full md:grid-rows-1 lg:grid-cols-2 gap-4 md:gap-[20px] items-stretch'>
        <div className='flex min-h-0 flex-col gap-[10px] md:gap-[15px]'>
          <div ref={manageContainerRef} className='relative'>
            <SiteManageMyMonitoringContainer
              layoutPrefix='site-desktop'
              siteLoading={loading}
              site={site}
              setView={setView}
              registerRefetch={registerRefetch}
            />
          </div>
        </div>
        <SiteEvents
          siteLoading={loading}
          site={site}
          registerRefetch={registerRefetch}
          maxHeight={shouldCapEventsToManageHeight ? manageHeight : undefined}
        />
      </div>
    </div>
  )
}
